// src/pages/exercise/components/UI/ConfettiCanvas.jsx
import { useEffect, useRef } from 'react';

const COLORS = [
  '#FF6B6B', '#FFD93D', '#6BCB77',
  '#4D96FF', '#FF922B', '#CC5DE8', '#F06595',
];

function randomBetween(a, b) {
  return a + Math.random() * (b - a);
}

function randomColor() {
  return COLORS[Math.floor(Math.random() * COLORS.length)];
}

function createSmallParticles(count = 80) {
  // Always burst from true center of screen
  const cx = window.innerWidth  / 2;
  const cy = window.innerHeight / 2;
  return Array.from({ length: count }, () => ({
    x:             cx + randomBetween(-15, 15),
    y:             cy + randomBetween(-15, 15),
    vx:            randomBetween(-7, 7),
    vy:            randomBetween(-12, -2),
    rotation:      randomBetween(0, Math.PI * 2),
    rotationSpeed: randomBetween(-0.18, 0.18),
    color:         randomColor(),
    shape:         Math.random() > 0.5 ? 'rect' : 'circle',
    w:             randomBetween(6, 13),
    h:             randomBetween(4, 9),
    life:          120,
    maxLife:       120,
    gravity:       0.3,
  }));
}

function createLargeWave(canvasWidth, count = 75) {
  return Array.from({ length: count }, () => ({
    x:             randomBetween(0, canvasWidth),
    y:             randomBetween(-60, -10),
    vx:            randomBetween(-1.8, 1.8),
    vy:            randomBetween(2.5, 6),
    rotation:      randomBetween(0, Math.PI * 2),
    rotationSpeed: randomBetween(-0.12, 0.12),
    color:         randomColor(),
    shape:         Math.random() > 0.4 ? 'rect' : 'circle',
    w:             randomBetween(7, 14),
    h:             randomBetween(5, 10),
    life:          180,
    maxLife:       180,
    gravity:       0.12,
  }));
}

export function ConfettiCanvas({ trigger, intensity = 'small', onComplete }) {
  const canvasRef   = useRef(null);
  const stateRef    = useRef({ particles: [], animId: null, active: false });
  const prevTrigger = useRef(trigger);

  useEffect(() => {
    if (trigger === prevTrigger.current) return;
    prevTrigger.current = trigger;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const s   = stateRef.current;

    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    if (intensity === 'small') {
      s.particles = [...s.particles, ...createSmallParticles()];
    } else {
      s.particles = [...s.particles, ...createLargeWave(canvas.width, 75)];
      setTimeout(() => {
        s.particles = [...s.particles, ...createLargeWave(canvas.width, 75)];
      }, 320);
      setTimeout(() => {
        s.particles = [...s.particles, ...createLargeWave(canvas.width, 70)];
      }, 650);
    }

    if (s.active) return;
    s.active = true;

    function drawParticle(p) {
      const alpha = p.life < 40 ? p.life / 40 : 1;
      ctx.globalAlpha = alpha;
      ctx.fillStyle   = p.color;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      if (p.shape === 'rect') {
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      } else {
        ctx.beginPath();
        ctx.ellipse(0, 0, p.w / 2, p.h / 2, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      ctx.globalAlpha = 1;
    }

    function tick() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      s.particles = s.particles.filter((p) => p.life > 0);

      for (const p of s.particles) {
        p.x  += p.vx;
        p.y  += p.vy;
        p.vy += p.gravity;
        p.vx *= 0.995;
        p.rotation += p.rotationSpeed;
        p.life     -= 1;
        drawParticle(p);
      }

      if (s.particles.length > 0) {
        s.animId = requestAnimationFrame(tick);
      } else {
        s.active = false;
        s.animId = null;
        onComplete?.();
      }
    }

    s.animId = requestAnimationFrame(tick);
    return () => { if (s.animId) cancelAnimationFrame(s.animId); };
  }, [trigger]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position:      'fixed',
        inset:         0,
        width:         '100vw',
        height:        '100vh',
        pointerEvents: 'none',
        zIndex:        9999,
      }}
    />
  );
}